import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';
import axios from 'axios';
import JalaliDatePicker, { toPersianDigits } from '../common/JalaliDatePicker';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PaidIcon from '@mui/icons-material/Paid';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { API_BASE_URL } from '../../config/constants';

const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '-';
  return moment(date).locale('fa').format('jYYYY/jMM/jDD HH:mm');
};

const formatJalaliShort = (iso: string): string => {
  if (!iso) return '';
  try { return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD')); }
  catch { return iso; }
};

const formatPrice = (price: number): string => new Intl.NumberFormat('fa-IR').format(price || 0);

const itemStatusLabels: Record<string, string> = {
  'pending': 'در انتظار',
  'processing': 'پردازش',
  'delivered': 'تحویل شده',
  'cancelled': 'لغو شده',
  'refunded': 'مرجوع شده',
  'disputed': 'اختلاف'
};

const getItemStatusTag = (status: string): string => {
  switch (status) {
    case 'pending': return 'tag-warning';
    case 'processing': return 'tag-info';
    case 'delivered': return 'tag-success';
    case 'cancelled': return 'tag-danger';
    case 'refunded': return 'tag-warning';
    case 'disputed': return 'tag-danger';
    default: return '';
  }
};

interface SoldProductOption {
  productId: string;
  title: string;
  salesCount: number;
}

interface SoldCapacityOption {
  capacityKey: string;
  name: string;
  salesCount: number;
}

interface SaleRow {
  _id: string;
  productId?: string;
  productTitle: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  status: string;
  storeName?: string;
  selectedCapacity?: {
    key?: string;
    name?: string;
    capacityKey?: string;
  };
  createdAt: string;
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  paymentStatus: string;
  buyer?: { _id?: string; name?: string; email?: string; phone?: string };
}

interface SalesSummary {
  totalSales: number;
  totalQuantity: number;
  totalAmount: number;
  uniqueBuyers: number;
}

interface SalesHistoryTabProps {
  onOpenOrder: (orderId: string, orderNumber: string) => void;
}

const getToken = () => localStorage.getItem('accessToken');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const SalesHistoryTab: React.FC<SalesHistoryTabProps> = ({ onOpenOrder }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Data
  const [rows, setRows] = useState<SaleRow[]>([]);
  const [summary, setSummary] = useState<SalesSummary>({ totalSales: 0, totalQuantity: 0, totalAmount: 0, uniqueBuyers: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  // Product autocomplete
  const [productQuery, setProductQuery] = useState('');
  const [productOptions, setProductOptions] = useState<SoldProductOption[]>([]);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [productSearching, setProductSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SoldProductOption | null>(null);
  const productBoxRef = useRef<HTMLDivElement | null>(null);

  // Capacity
  const [capacities, setCapacities] = useState<SoldCapacityOption[]>([]);
  const [capacityKey, setCapacityKey] = useState('');

  // Other filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // 🎯 وضعیت ردیابی فروش (ثبت فروشنده): '' همه / 'yes' ثبت‌شده / 'no' بدون ردیابی
  // پیش‌فرض فقط سفارش‌های پرداخت‌شده (فروش واقعی)
  const [paidFilter, setPaidFilter] = useState<'paid' | 'all'>('paid');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const buildQuery = useCallback((pageArg: number, limitArg: number) => {
    let q = `page=${pageArg}&limit=${limitArg}`;
    if (selectedProduct) q += `&productId=${selectedProduct.productId}`;
    if (capacityKey) q += `&capacityKey=${encodeURIComponent(capacityKey)}`;
    if (statusFilter) q += `&status=${statusFilter}`;
    if (dateFrom) q += `&startDate=${dateFrom}`;
    if (dateTo) q += `&endDate=${dateTo}`;
    if (search) q += `&search=${encodeURIComponent(search)}`;
    if (paidFilter === 'all') q += '&paidOnly=false';
    return q;
  }, [selectedProduct, capacityKey, statusFilter, dateFrom, dateTo, search, paidFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/orders/sales-history?${buildQuery(page, limit)}`, authHeaders());
      if (res.data.status === 'success') {
        setRows(res.data.data.docs || []);
        setTotal(res.data.data.totalDocs || 0);
        setSummary(res.data.data.summary || { totalSales: 0, totalQuantity: 0, totalAmount: 0, uniqueBuyers: 0 });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در دریافت تاریخچه فروش', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, buildQuery, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Product autocomplete — debounce 400ms
  useEffect(() => {
    if (selectedProduct && productQuery === selectedProduct.title) return;
    const t = setTimeout(async () => {
      if (!productDropdownOpen) return;
      setProductSearching(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/admin/orders/sales-history/products?search=${encodeURIComponent(productQuery)}${paidFilter === 'all' ? '&paidOnly=false' : ''}`,
          authHeaders()
        );
        if (res.data.status === 'success') setProductOptions(res.data.data || []);
      } catch { /* silent */ }
      finally { setProductSearching(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuery, productDropdownOpen, paidFilter]);

  // Close product dropdown on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (productBoxRef.current && !productBoxRef.current.contains(e.target as Node)) {
        setProductDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadCapacities = useCallback(async (productId: string) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/admin/orders/sales-history/capacities?productId=${productId}${paidFilter === 'all' ? '&paidOnly=false' : ''}`,
        authHeaders()
      );
      if (res.data.status === 'success') setCapacities(res.data.data || []);
    } catch { setCapacities([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidFilter]);

  const selectProduct = async (p: SoldProductOption) => {
    setSelectedProduct(p);
    setProductQuery(p.title);
    setProductDropdownOpen(false);
    setCapacityKey('');
    setPage(1);
    loadCapacities(p.productId);
  };

  // با تغییر فیلتر پرداخت، شمارنده‌های حجم / سایز هم به‌روز شوند
  useEffect(() => {
    if (selectedProduct) loadCapacities(selectedProduct.productId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidFilter]);

  const clearProduct = () => {
    setSelectedProduct(null);
    setProductQuery('');
    setCapacities([]);
    setCapacityKey('');
    setPage(1);
  };

  const capacityOptions = useMemo(() => {
    const seen = new Map<string, SoldCapacityOption>();
    capacities
      .forEach(c => {
        const existing = seen.get(c.capacityKey);
        if (existing) existing.salesCount += c.salesCount;
        else seen.set(c.capacityKey, { ...c });
      });
    return Array.from(seen.values());
  }, [capacities]);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const clearFilters = () => {
    clearProduct();
    setSearch('');
    setSearchInput('');
    setStatusFilter('');
    setPaidFilter('paid');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasActiveFilters = !!(selectedProduct || capacityKey || search || statusFilter || paidFilter === 'all' || dateFrom || dateTo);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const capacityLabel = (row: SaleRow): string => {
    const c = row.selectedCapacity;
    if (!c?.name) return '-';
    return c.name;
  };

  // خروجی CSV (اکسل) از کل نتایج فیلترشده — تا سقف ۲۰۰۰ ردیف
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/orders/sales-history?${buildQuery(1, 2000)}`, authHeaders());
      const docs: SaleRow[] = res.data?.data?.docs || [];
      if (!docs.length) {
        enqueueSnackbar('نتیجه‌ای برای خروجی گرفتن وجود ندارد', { variant: 'info' });
        return;
      }
      const headers = ['تاریخ', 'شماره سفارش', 'محصول', 'حجم / سایز', 'خریدار', 'موبایل', 'ایمیل', 'فروشگاه', 'تعداد', 'مبلغ (تومان)', 'وضعیت آیتم', 'وضعیت پرداخت'];
      const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = docs.map(r => {
        return [
        moment(r.createdAt).format('jYYYY/jMM/jDD HH:mm'),
        r.orderNumber,
        r.productTitle,
        r.selectedCapacity?.name || '',
        r.buyer?.name || '',
        r.buyer?.phone || '',
        r.buyer?.email || '',
        r.storeName || '',
        r.quantity ?? 1,
        r.totalPrice ?? 0,
        itemStatusLabels[r.status] || r.status,
        r.paymentStatus === 'paid' ? 'پرداخت شده' : r.paymentStatus === 'failed' ? 'ناموفق' : r.paymentStatus === 'refunded' ? 'مرجوع شده' : 'در انتظار'
        ].map(escape).join(',');
      });
      const csv = '﻿' + [headers.map(escape).join(','), ...lines].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-history-${moment().format('jYYYY-jMM-jDD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (docs.length >= 2000) {
        enqueueSnackbar('خروجی به ۲۰۰۰ ردیف اول محدود شد — بازه تاریخ را کوچک‌تر کنید', { variant: 'warning' });
      }
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'خطا در تهیه خروجی اکسل', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Summary stats for the current filter */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><ReceiptLongIcon /></div>
          <div>
            <p className="entity-stat-label">تعداد فروش</p>
            <p className="entity-stat-value">{toPersianDigits(String(summary.totalSales))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><Inventory2Icon /></div>
          <div>
            <p className="entity-stat-label">تعداد واحد فروخته‌شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(summary.totalQuantity))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><PaidIcon /></div>
          <div>
            <p className="entity-stat-label">مجموع فروش (تومان)</p>
            <p className="entity-stat-value">{formatPrice(summary.totalAmount)}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><PeopleAltIcon /></div>
          <div>
            <p className="entity-stat-label">خریداران یکتا</p>
            <p className="entity-stat-value">{toPersianDigits(String(summary.uniqueBuyers))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          {/* Product autocomplete */}
          <div className="filter-group" ref={productBoxRef} style={{ position: 'relative' }}>
            <label className="filter-label">
              <Inventory2Icon fontSize="small" />
              محصول
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="نام محصول را تایپ کنید..."
                value={productQuery}
                onChange={(e) => {
                  setProductQuery(e.target.value);
                  setProductDropdownOpen(true);
                  if (selectedProduct && e.target.value !== selectedProduct.title) {
                    setSelectedProduct(null);
                    setCapacities([]);
                    setCapacityKey('');
                                  }
                }}
                onFocus={() => setProductDropdownOpen(true)}
              />
              {selectedProduct ? (
                <button
                  onClick={clearProduct}
                  style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: 2, display: 'flex' }}
                  title="حذف محصول"
                >
                  <CloseIcon fontSize="small" />
                </button>
              ) : (
                <span className="filter-search-icon">
                  {productSearching ? <CircularProgress size={14} /> : <SearchIcon fontSize="small" />}
                </span>
              )}
            </div>
            {productDropdownOpen && (productOptions.length > 0 || productSearching) && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, left: 0, zIndex: 30,
                background: 'var(--color-bg-paper, #fff)', border: '1px solid var(--color-border)',
                borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: 'auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
              }}>
                {productSearching && productOptions.length === 0 ? (
                  <div style={{ padding: 12, fontSize: 12, color: 'var(--color-text-secondary)', textAlign: 'center' }}>در حال جستجو...</div>
                ) : productOptions.map(p => (
                  <button
                    key={p.productId}
                    onClick={() => selectProduct(p)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                      width: '100%', padding: '8px 12px', background: 'none', border: 'none',
                      borderBottom: '1px solid var(--color-border)', cursor: 'pointer', textAlign: 'right', fontSize: 13
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                      {toPersianDigits(String(p.salesCount))} فروش
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Capacity (only after product selection) */}
          {selectedProduct && capacityOptions.length > 0 && (
            <div className="filter-group">
              <label className="filter-label">حجم / سایز</label>
              <select className="filter-field" value={capacityKey} onChange={(e) => { setCapacityKey(e.target.value); setPage(1); }}>
                <option value="">همه حجم‌ها / سایزها</option>
                {capacityOptions.map(c => (
                  <option key={c.capacityKey} value={c.capacityKey}>
                    {c.name} ({toPersianDigits(String(c.salesCount))} فروش)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="خریدار، موبایل، شماره سفارش، ایمیل..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت پرداخت</label>
            <select className="filter-field" value={paidFilter} onChange={(e) => { setPaidFilter(e.target.value as 'paid' | 'all'); setPage(1); }}>
              <option value="paid">فقط پرداخت‌شده (فروش واقعی)</option>
              <option value="all">همه (شامل پرداخت‌نشده)</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت آیتم</label>
            <select className="filter-field" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">همه</option>
              <option value="pending">در انتظار</option>
              <option value="processing">پردازش</option>
              <option value="delivered">تحویل شده</option>
              <option value="cancelled">لغو شده</option>
              <option value="refunded">مرجوع شده</option>
              <option value="disputed">اختلاف</option>
            </select>
          </div>


          <div className="filter-group">
            <label className="filter-label">از تاریخ</label>
            <JalaliDatePicker
              value={dateFrom}
              onChange={(v) => { setDateFrom(v); setPage(1); }}
              placeholder="تاریخ شروع"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">تا تاریخ</label>
            <JalaliDatePicker
              value={dateTo}
              onChange={(v) => { setDateTo(v); setPage(1); }}
              placeholder="تاریخ پایان"
              height={40}
              outputDateOnly
            />
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            <button className="filter-btn outline" onClick={exportExcel} disabled={exporting || loading}>
              {exporting ? <CircularProgress size={14} /> : <FileDownloadIcon fontSize="small" />}
              خروجی اکسل
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {selectedProduct && <span className="filter-tag">محصول: {selectedProduct.title}<button onClick={clearProduct}>×</button></span>}
            {capacityKey && <span className="filter-tag">حجم / سایز: {capacityOptions.find(c => c.capacityKey === capacityKey)?.name || capacityKey}<button onClick={() => setCapacityKey('')}>×</button></span>}
            {search && <span className="filter-tag">جستجو: {search}<button onClick={() => { setSearch(''); setSearchInput(''); }}>×</button></span>}
            {statusFilter && <span className="filter-tag">وضعیت: {itemStatusLabels[statusFilter]}<button onClick={() => setStatusFilter('')}>×</button></span>}
            {paidFilter === 'all' && <span className="filter-tag">پرداخت: همه<button onClick={() => setPaidFilter('paid')}>×</button></span>}
            {dateFrom && <span className="filter-tag">از: {formatJalaliShort(dateFrom)}<button onClick={() => setDateFrom('')}>×</button></span>}
            {dateTo && <span className="filter-tag">تا: {formatJalaliShort(dateTo)}<button onClick={() => setDateTo('')}>×</button></span>}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : rows.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><ReceiptLongIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">فروشی با این فیلترها یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>شماره سفارش</th>
                  <th>محصول</th>
                  <th>حجم / سایز</th>
                  <th>خریدار</th>
                  <th>فروشگاه</th>
                  <th>تعداد</th>
                  <th>مبلغ</th>
                  <th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row._id}>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</td>
                    <td>
                      <Tooltip title="نمایش جزئیات سفارش" arrow>
                        <button
                          onClick={() => onOpenOrder(row.orderId, row.orderNumber)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontWeight: 600, fontSize: 13, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <InfoOutlinedIcon style={{ fontSize: 14 }} />
                          {row.orderNumber}
                        </button>
                      </Tooltip>
                    </td>
                    <td style={{ fontSize: 13, maxWidth: 200 }}>{row.productTitle}</td>
                    <td>
                      <div style={{ fontSize: 12 }}>{capacityLabel(row)}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{row.buyer?.name || row.buyer?.email || '-'}</div>
                      {row.buyer?.phone && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', direction: 'ltr', textAlign: 'right' }}>{toPersianDigits(row.buyer.phone)}</div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{row.storeName || '-'}</td>
                    <td style={{ fontSize: 13 }}>{toPersianDigits(String(row.quantity ?? 1))}</td>
                    <td style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{formatPrice(row.totalPrice)} تومان</td>
                    <td>
                      <span className={`tag tag-sm ${getItemStatusTag(row.status)}`}>
                        {itemStatusLabels[row.status] || row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {rows.map((row) => (
                <div key={row._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      <Inventory2Icon />
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{row.productTitle}</div>
                      <div className="entity-card-desc">
                        {capacityLabel(row)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary)', marginTop: 4 }}>
                        {formatPrice(row.totalPrice)} تومان
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${getItemStatusTag(row.status)}`}>
                        {itemStatusLabels[row.status] || row.status}
                      </span>
                    </div>
                  </div>
                  <div style={{ padding: '0 12px 8px', display: 'flex', gap: '4px 12px', flexWrap: 'wrap', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    <span>خریدار: {row.buyer?.name || row.buyer?.email || '-'}</span>
                    {row.buyer?.phone && <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{toPersianDigits(row.buyer.phone)}</span>}
                    <span>{formatDate(row.createdAt)}</span>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn" onClick={() => onOpenOrder(row.orderId, row.orderNumber)}>
                      <InfoOutlinedIcon fontSize="small" /> سفارش {row.orderNumber}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از {toPersianDigits(String(total))}
              </div>
              <div className="pagination-controls">
                <button className="pagination-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{toPersianDigits(String(page))} / {toPersianDigits(String(totalPages))}</span>
                <button className="pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SalesHistoryTab;
